/**
 * English UI catalogue of the Assets panel (W2-10 projects-data; namespace 'assets').
 * Imports only src/i18n/define.ts (UI message catalogue zone). ICU-lite syntax (src/i18n/icu.ts).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('assets', {
  // ---- panel ----
  panelTitle: 'Assets',
  summary: '{count, plural, one {# file} other {# files}} · {size}',
  addFile: 'Add file',
  usedListLabel: 'Files in use',
  unusedListLabel: 'Unused files',
  unusedTitle: 'Unused ({count})',
  removeAllUnused: 'Remove all unused',
  emptyTitle: 'No files yet',
  emptyBody: 'The song, the illustration, line images, GIFs, hit sounds, shaders and videos of the chart appear here.',
  noProjectTitle: 'No chart open',
  noProjectBody: 'Open a chart to see its files.',
  dimensions: '{width} x {height} px',
  namingRules:
    'Names in a chart package use plain ASCII and never end in "click.png" (Phira would treat the package as a skin); a custom hit sound is never named exactly tap.mp3, flick.mp3 or drag.mp3 (Phira plays its built-in sound for those names).',
  chooseFile: 'Choose file...',
  replaceLabel: 'Replace {name}',
  renameAction: 'Rename {name}',
  renameLabel: 'File name in the package',
  downscaleLabel: 'Make {name} smaller',
  downloadLabel: 'Download {name}',
  removeLabel: 'Remove {name}',

  // ---- roles ----
  roleMusic: 'song',
  roleIllustration: 'illustration',
  roleTexture: 'image',
  roleGif: 'GIF',
  roleHitsound: 'hit sound',
  roleShader: 'shader',
  roleVideo: 'video',
  roleFont: 'font',
  roleOther: 'other file',

  // ---- usage ----
  usedBy: 'used by {users}',
  usedSong: 'the song of the chart',
  usedIllustration: 'the illustration',
  usedUnlockVideo: 'the unlock video',
  usedLines: '{count, plural, one {# line} other {# lines}}',
  usedNotes: '{count, plural, one {# note} other {# notes}}',
  usedEffects: '{count, plural, one {# effect} other {# effects}}',
  usedVideos: '{count, plural, one {# video} other {# videos}}',
  unused: 'unused',

  // ---- problems ----
  badgeMissing: 'Missing',
  badgeMissingTip:
    'The chart uses this file but it was not in the imported package. Phira cannot load the chart until a file is chosen or the references are removed.',
  badgeUndecodable: 'Phira cannot read it',
  badgeUndecodableTip:
    'Phira cannot decode this format ({codec}). Replace it with OGG, MP3, WAV or FLAC sound, or PNG, JPG or WebP images.',
  badgeName: 'Name',
  nameReserved: 'Phira reserves this name ("export.json" or "line.png" at the top of the package). Rename the file.',
  nameClickPng: 'A name ending in "click.png" makes Phira treat the package as a skin. Rename the file.',
  nameBuiltinHitsound:
    'Phira plays its built-in sound for a hit sound named tap.mp3, flick.mp3 or drag.mp3. Rename the file.',
  nameInvalid:
    'This name cannot be used in a package (empty folder, "..", a leading "/" or a backslash). Rename the file.',
  badgeNonAscii: 'Non-ASCII name',
  badgeNonAsciiTip: 'Some tools mangle names outside plain ASCII when unpacking. A plain name is safer.',
  badgeLarge: '{width} x {height}',
  badgeLargeTip:
    'Phira draws images without mipmaps: images larger than 2048 px shimmer when shrunk and use more memory. Make it smaller.',

  // ---- add ----
  addTxnOne: 'Add file {name}',
  addTxnMany: 'Add {count} files',
  addedOne: 'Added {name}.',
  addedMany: 'Added {count} files.',
  alreadyAdded: '{name} is already in the chart as {path}.',
  readFailed: '{name} could not be read: {reason}',
  storeFailed: '{name} could not be stored: {reason}',
  wrongKind: '{name} cannot be used as a {role}.',
  largeTitle: '{name} is {width} x {height} px',
  largeBody:
    'Phira draws images without mipmaps, so images larger than {limit} px shimmer when shrunk. A smaller copy looks the same on phones and loads faster.',
  downscaleTo: 'Make it {side} px on the longest side',
  keepOriginal: 'Keep the original size',
  keepOriginalHelp: 'The checker keeps a tip about it.',
  downscaleFailed: 'The image could not be made smaller: {reason}',

  // ---- replace ----
  replaceTxn: 'Replace {from} with {to}',
  replaced: 'Replaced {from} with {to}.',
  sameFile: '{name} is the same file: nothing changed.',
  fileRestored: '{name} is back: the file is stored in this browser again.',
  songRestored: 'The song {name} is back: it is stored in this browser again and loaded for playback.',

  // ---- files missing from this browser's storage (UX-065, UX-066) ----
  storageMissingOne:
    "The file {name} is missing from this browser's storage. Choose the same file again to restore it.",
  storageMissingMany:
    "{count, plural, one {# file is} other {# files are}} missing from this browser's storage: {names}. Choose them again in Assets.",
  showFiles: 'Show files',
  badgeStorageMissing: 'Missing from this browser',
  badgeStorageMissingTip:
    'The chart lists this file, but the browser no longer has its bytes. Choose the same file again to restore it; until then the chart cannot be exported.',
  gone: 'This file is no longer in the chart.',
  songUndecodable: '{name} cannot be played in this browser, so it was not used as the song.',
  songLoadFailed: 'The new song could not be loaded for playback: {reason}',
  previewTitle: 'The song-select preview no longer fits the song',
  previewBody:
    'The new song is {length} long, but the preview plays {start} to {end}. Phira refuses to open a chart whose preview ends after the song.',
  previewFix: 'Move the preview inside the song',
  previewFixHelp: 'The preview becomes {start} to {end}.',
  previewKeep: 'Keep the preview as it is',
  previewKeepHelp: 'The Problems tab will show it as an error until you change it.',

  // ---- remove ----
  removeTxn: 'Remove file {name}',
  removed: 'Removed {name}.',
  removeUsedTitle: 'Remove {name}?',
  removeUsedBody:
    'It is {users}. Those references are removed too: lines go back to the built-in line image, notes to their normal sound, and effects or videos using it are deleted. You can undo this.',
  removeSongBody:
    'It is {users}. The chart will have no song until you choose one, and cannot be exported without it. You can undo this.',
  removeConfirm: 'Remove',
  removeUnusedTitle: 'Remove {count, plural, one {# unused file} other {# unused files}}?',
  removeUnusedBody: 'Nothing in the chart uses them. You can undo this.',
  removeUnusedTxn: 'Remove {count, plural, one {# unused file} other {# unused files}}',
  removedUnused: 'Removed {count, plural, one {# unused file} other {# unused files}}.',
  noneUnused: 'Every file is in use.',

  // ---- rename, downscale, download ----
  renameTxn: 'Rename file {from} to {to}',
  renamed: 'Renamed to {name}.',
  downscaleTxn: 'Make {name} {side} px',
  downscaled: '{name} is now {side} px on its longest side.',
  notLarge: '{name} is not larger than {limit} px.',
  missingNoDownload: 'This file is missing: there is nothing to download.',

  // ---- sizes ----
  sizeB: '{size} B',
  sizeKB: '{size} KB',
  sizeMB: '{size} MB',
  sizeGB: '{size} GB',
  sizeTB: '{size} TB',

  // ---- commands ----
  cmdShow: 'Assets',
  cmdAdd: 'Add file to the chart...',
  cmdReplace: 'Replace file...',
  cmdRemove: 'Remove file',
  cmdRename: 'Rename file',
  cmdDownscale: 'Make image smaller...',
  cmdDownload: 'Download file',
  cmdRemoveUnused: 'Remove unused files',
  reasonNoProject: 'Open a chart first.',
  pickAsset: 'Pick a file in the Assets tab.',

  // ---- help ----
  helpTitle: 'Files of a chart',
  helpBody:
    'The Assets tab lists every file of the chart: the song, the illustration, line images and GIFs, hit sounds, shaders and videos, with who uses each one. Replace a file to point every use at another one (missing files, a new song), rename it, make large images smaller, or remove it. Files are stored once even when several charts use them. Phira needs plain ASCII names that never end in "click.png", and plays its own sound for hit sounds named tap.mp3, flick.mp3 or drag.mp3.',
});

export default catalogOf(m);
